/**
 * Palette — SPEC §15 art-direction tokens, the sRGB<->linear transfer, and the seeded
 * cold colourway roll that fills a shark's eight voxel colour slots.
 */

import type { Rng } from '../core/Rng.ts';

export const TOKENS: {
  abyss: number; hydro: number; caustic: number; bone: number;
  corrode: number; verdigris: number; alarm: number;
} = {
  abyss: 0x04121c,
  hydro: 0x0e5c6e,
  caustic: 0x7fe3d4,
  bone: 0xe8e2d0,
  corrode: 0xb7642a,
  verdigris: 0x4e8c6a,
  alarm: 0xff3b1f,
};

export type Rgb = readonly [number, number, number];

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * The real IEC 61966-2-1 piecewise transfer, not pow(c, 2.2). The renderer works in linear
 * space under ACES tone mapping, where the gamma approximation visibly muddies mid-tones —
 * exactly the 0.2..0.5 band every shark flank lives in.
 */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function toByte(c: number): number {
  const b = Math.round(clamp01(c) * 255);
  return b < 0 ? 0 : b > 255 ? 255 : b;
}

/** sRGB hex (0xRRGGBB) → linear RGB triple */
export function hexToLinear(hex: number): Rgb {
  const h = hex >>> 0;
  return [
    srgbToLinear(((h >>> 16) & 0xff) / 255),
    srgbToLinear(((h >>> 8) & 0xff) / 255),
    srgbToLinear((h & 0xff) / 255),
  ];
}

export function linearToHex(c: Rgb): number {
  return (
    (toByte(linearToSrgb(clamp01(c[0]))) << 16) |
    (toByte(linearToSrgb(clamp01(c[1]))) << 8) |
    toByte(linearToSrgb(clamp01(c[2])))
  ) >>> 0;
}

export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const k = clamp01(t);
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}

/** multiply value, keep hue. v > 1 brightens */
export function shade(c: Rgb, v: number): Rgb {
  return [clamp01(c[0] * v), clamp01(c[1] * v), clamp01(c[2] * v)];
}

/** Rec.709 relative luminance in linear space — the countershading ordering is judged on this. */
function lum(c: Rgb): number {
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/**
 * Fixed slot layout for every shark. paletteIndex stored in the voxel grid is
 * (slot + 1); 0 means empty.
 */
export const SLOT = {
  DORSAL: 0,     // genome.palette[0]
  FLANK: 1,      // genome.palette[1]
  VENTRAL: 2,    // genome.palette[2]
  GILL: 3,       // dark slit
  TOOTH: 4,      // bone
  EYE: 5,        // near-black with a wet highlight value
  FIN_EDGE: 6,   // darkened dorsal colour, used on fin trailing edges
  SCAR: 7,       // reserved, currently a lightened flank
} as const;
export const SLOT_COUNT = 8;

/** How far the derived slots sit from their parent colours. */
const GILL_TO_ABYSS = 0.66;
const GILL_SHADE = 0.62;
const TOOTH_TO_WHITE = 0.16;
const EYE_SHADE = 0.30;
const EYE_WET = 0.11;
const FIN_EDGE_SHADE = 0.52;
const FIN_EDGE_TO_ABYSS = 0.22;
const SCAR_TO_BONE = 0.36;

function put(out: Float32Array, slot: number, c: Rgb): void {
  const i = slot * 3;
  out[i] = c[0];
  out[i + 1] = c[1];
  out[i + 2] = c[2];
}

/** Build the flat linear-RGB table the mesher indexes into. length = SLOT_COUNT * 3. */
export function buildPalette(genomePalette: readonly [number, number, number]): Float32Array {
  const out = new Float32Array(SLOT_COUNT * 3);
  const dorsal = hexToLinear(genomePalette[0]);
  const flank = hexToLinear(genomePalette[1]);
  const ventral = hexToLinear(genomePalette[2]);
  const abyss = hexToLinear(TOKENS.abyss);
  const bone = hexToLinear(TOKENS.bone);

  put(out, SLOT.DORSAL, dorsal);
  put(out, SLOT.FLANK, flank);
  put(out, SLOT.VENTRAL, ventral);
  // Gills read as shadow, not as ink: they keep the body's hue so they sit in the material.
  put(out, SLOT.GILL, shade(mixRgb(dorsal, abyss, GILL_TO_ABYSS), GILL_SHADE));
  put(out, SLOT.TOOTH, mixRgb(bone, [1, 1, 1], TOOTH_TO_WHITE));
  // A pure-black eye dies under ACES; the caustic bleed is the wet glint that keeps it alive.
  put(out, SLOT.EYE, mixRgb(shade(abyss, EYE_SHADE), hexToLinear(TOKENS.caustic), EYE_WET));
  // Fin trailing edges are the silhouette's outline — darker than the dorsal, same family.
  put(out, SLOT.FIN_EDGE, mixRgb(shade(dorsal, FIN_EDGE_SHADE), abyss, FIN_EDGE_TO_ABYSS));
  put(out, SLOT.SCAR, mixRgb(flank, bone, SCAR_TO_BONE));

  return out;
}

export type PaletteHint = 'cold' | 'pale' | 'dark' | 'bronze';

interface FamilySpec {
  hue: [number, number];
  dorsalSat: [number, number];
  dorsalVal: [number, number];
  flankSat: [number, number];
  flankVal: [number, number];
  /** how much flank colour bleeds into the bone ventral */
  boneMix: [number, number];
  /** final value multiplier on the ventral — "near bone, but knocked back" */
  ventralShade: [number, number];
}

/**
 * Every hue band here is deliberately outside 0..45° at any meaningful saturation, and every
 * saturation is capped well under alarm's 0.88. #FF3B1F is structurally unreachable from this
 * roll, which is the point: it is reserved for danger and nothing else (SPEC §15). The rose
 * sub-band declared below is the single documented exception and carries its own harder cap.
 * bronze stays in the olive/khaki band rather than corrode's 26° so enemies keep their own
 * warm identity — a bronze shark is drab, not rusted.
 */
const FAMILIES: Record<PaletteHint, FamilySpec> = {
  cold: {
    hue: [196, 220],
    dorsalSat: [0.22, 0.36], dorsalVal: [0.17, 0.27],
    flankSat: [0.14, 0.24], flankVal: [0.36, 0.50],
    boneMix: [0.10, 0.22], ventralShade: [0.86, 0.98],
  },
  pale: {
    hue: [186, 214],
    dorsalSat: [0.13, 0.23], dorsalVal: [0.30, 0.40],
    flankSat: [0.07, 0.15], flankVal: [0.54, 0.64],
    boneMix: [0.05, 0.13], ventralShade: [0.92, 1.0],
  },
  dark: {
    hue: [200, 226],
    dorsalSat: [0.26, 0.42], dorsalVal: [0.075, 0.13],
    flankSat: [0.20, 0.32], flankVal: [0.19, 0.28],
    boneMix: [0.20, 0.34], ventralShade: [0.70, 0.84],
  },
  bronze: {
    hue: [46, 94],
    dorsalSat: [0.20, 0.34], dorsalVal: [0.16, 0.25],
    flankSat: [0.15, 0.25], flankVal: [0.32, 0.44],
    boneMix: [0.16, 0.28], ventralShade: [0.82, 0.94],
  },
};

const HINT_WEIGHTS: readonly PaletteHint[] = [
  'cold', 'cold', 'cold', 'cold', 'cold', 'pale', 'pale', 'dark', 'bronze',
];

/** Hard ceiling: no shark is ever more chromatic than this. Alarm sits at 0.88. */
const MAX_SHARK_SAT = 0.42;
/** Chance a cold roll lands in the rarer metallic-indigo sub-band (mako, cookiecutter). */
const COLD_INDIGO_CHANCE = 0.28;
const INDIGO_HUE: [number, number] = [230, 250];
const INDIGO_SAT_BIAS = 0.06;
/**
 * The one deliberate warm exception: the goblin shark is "pinkish-pale" (SPEC §7). It sits on
 * the rose side of red, never the orange side where alarm lives, and is capped at a sixth of
 * alarm's chroma — it reads as translucent flesh over cartilage, not as a danger colour.
 */
const PALE_ROSE_CHANCE = 0.4;
const PALE_ROSE_HUE: [number, number] = [338, 358];
const PALE_ROSE_SAT_CAP = 0.14;

/** Countershading contract: dorsal must sit far under the ventral, flank strictly between. */
const DORSAL_LUM_CEILING = 0.35;
const DORSAL_LUM_TARGET = 0.28;
const FLANK_LUM_CEILING = 0.62;
const FLANK_LUM_FLOOR = 1.6;

function hsvToHex(h: number, s: number, v: number): number {
  const hh = ((((h % 360) + 360) % 360) / 60);
  const i = Math.floor(hh);
  const f = hh - i;
  const p = v * (1 - s);
  const q = v * (1 - s * f);
  const t = v * (1 - s * (1 - f));
  let r: number, g: number, b: number;
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    default: r = v; g = p; b = q; break;
  }
  return ((toByte(r) << 16) | (toByte(g) << 8) | toByte(b)) >>> 0;
}

function span(rng: Rng, r: readonly [number, number]): number {
  return rng.range(r[0], r[1]);
}

/** Seeded, art-directed shark colourway. Cold blues/greys/bronzes — never enemy-warm. */
export function rollSharkPalette(
  rng: Rng,
  hint?: 'cold' | 'pale' | 'dark' | 'bronze',
): [number, number, number] {
  const family: PaletteHint = hint ?? rng.pick(HINT_WEIGHTS);
  const spec = FAMILIES[family];

  let hue = span(rng, spec.hue);
  let satBias = 0;
  let satCap = MAX_SHARK_SAT;
  if (family === 'cold' && rng.bool(COLD_INDIGO_CHANCE)) {
    hue = span(rng, INDIGO_HUE);
    satBias = INDIGO_SAT_BIAS;
  } else if (family === 'pale' && rng.bool(PALE_ROSE_CHANCE)) {
    hue = span(rng, PALE_ROSE_HUE);
    satCap = PALE_ROSE_SAT_CAP;
  }

  const dorsalHex = hsvToHex(
    hue,
    Math.min(satCap, span(rng, spec.dorsalSat) + satBias),
    span(rng, spec.dorsalVal),
  );
  // Real countershading drifts warmer and greener down the flank, never cooler.
  const flankHex = hsvToHex(
    hue + rng.range(-5, 11),
    Math.min(satCap, span(rng, spec.flankSat) + satBias * 0.5),
    span(rng, spec.flankVal),
  );

  let dorsal = hexToLinear(dorsalHex);
  let flank = hexToLinear(flankHex);
  // The belly is bone stained by the body colour, then knocked back so it never blows out
  // to paper-white under the key light.
  const ventral = shade(
    mixRgb(hexToLinear(TOKENS.bone), flank, span(rng, spec.boneMix)),
    span(rng, spec.ventralShade),
  );

  // Enforce the dark→light ramp. Countershading is how the silhouette reads from a 62° camera,
  // so it is an invariant, not a preference: any roll that violates it gets pulled into line.
  const lv = Math.max(lum(ventral), 1e-5);
  const ld = Math.max(lum(dorsal), 1e-5);
  if (ld > lv * DORSAL_LUM_CEILING) dorsal = shade(dorsal, (lv * DORSAL_LUM_TARGET) / ld);
  const lf = Math.max(lum(flank), 1e-5);
  const hiF = lv * FLANK_LUM_CEILING;
  const loF = Math.max(lum(dorsal), 1e-5) * FLANK_LUM_FLOOR;
  if (lf > hiF) flank = shade(flank, hiF / lf);
  else if (lf < loF) flank = shade(flank, loF / lf);

  return [linearToHex(dorsal), linearToHex(flank), linearToHex(ventral)];
}
